import { Button } from '@kluein/klue-ui';
import { Box } from 'grommet';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppUIContext } from './appUIContext';
import { useSessionIdentityChange } from './useSessionIdentityChange';

/**
 * Component that warns about stale session identity across browser tabs.
 *
 * When the session identity changes in one tab (e.g. impersonation, sign-out,
 * or company switch), other tabs still show stale data. When useSessionIdentityChange
 * reports a change, this shows a persistent toast offering to reload.
 *
 * Must be rendered inside AppUIProvider to have access to the toast context. Render it once per
 * page: App.tsx mounts it for Current Compete, and Compete 2.0 has its own SessionChangedNotifier.
 */
export function ImpersonationGuardNotifier(): null {
  const { t } = useTranslation(['Session']);
  const { toast } = useAppUIContext();
  const toastIdRef = useRef<string | null>(null);
  const dismissedRef = useRef<boolean>(false);

  // Store latest toast ref to avoid stale closures
  const toastRef = useRef(toast);

  useEffect(() => {
    toastRef.current = toast;
  });

  const handleReload = useCallback((): void => {
    window.location.reload();
  }, []);

  const handleDismiss = useCallback((): void => {
    dismissedRef.current = true;

    if (toastIdRef.current) {
      toastRef.current.close(toastIdRef.current, true);
      toastIdRef.current = null;
    }
  }, []);

  useSessionIdentityChange(() => {
    // Already dismissed or already showing the toast
    if (dismissedRef.current || toastIdRef.current) {
      return;
    }

    toastIdRef.current = toastRef.current.open({
      title: t('Session:identityGuard.title'),
      message: t('Session:identityGuard.message'),
      action: (
        <Box direction="row" gap="small" justify="end" align="center">
          <Button
            variant="primary"
            onClick={handleReload}
            label={t('Session:identityGuard.reloadNow')}
          />
          <Button
            variant="plain"
            onClick={handleDismiss}
            label={t('Session:identityGuard.reloadLater')}
          />
        </Box>
      ),
    });
  });

  useEffect(() => {
    return () => {
      if (toastIdRef.current) {
        toastRef.current.close(toastIdRef.current, true);
        toastIdRef.current = null;
      }
    };
  }, []);

  return null;
}
